// עורך ערכת מותג. שמירה תמיד יוצרת גרסה חדשה (POST /api/brands/:id/versions).
import { useMemo, useState } from 'react';
import type { BrandKit } from '@bb/schema';
import { ErrorBox, Field } from '../ui';
import { Check, HEX_RE } from './common';
import { AssetPicker, AssetThumb, useClientAssets, type Asset } from './AssetLibrary';

export const LOGO_VARIANTS: Record<string, string> = { primary: 'ראשי', light: 'בהיר (לרקע כהה)', dark: 'כהה (לרקע בהיר)', icon: 'אייקון', other: 'אחר' };
export const COLOR_ROLES: Record<string, string> = { primary: 'ראשי', secondary: 'משני', accent: 'הדגשה', text: 'טקסט', background: 'רקע', cta: 'CTA', other: 'אחר' };
export const FONT_ROLES: Record<string, string> = { headline: 'כותרות', body: 'טקסט רץ', cta: 'CTA', other: 'אחר' };

export const EMPTY_KIT: BrandKit = { logos: [], colors: [], fonts: [], logoClearSpacePx: null, logoMinWidthPx: null, minFontSizePx: 10, imageStyle: '', animationStyle: '', contentGuidelines: '', guideText: '', approvedExampleAssetIds: [] };

export function normalizeKit(k: Partial<BrandKit> | null | undefined): BrandKit { return { ...EMPTY_KIT, ...(k ?? {}) } as BrandKit; }

export function BrandKitEditor({ clientId, initial, busy, error, onSave, onCancel }: { clientId: string; initial: BrandKit; busy: boolean; error: string | null; onSave: (kit: BrandKit, note: string) => void; onCancel: () => void }) {
  const [kit, setKit] = useState<BrandKit>(() => structuredClone(normalizeKit(initial)));
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState<null | 'logo' | 'example'>(null);
  const { data: assets } = useClientAssets(clientId, { status: 'all' });
  const byId = useMemo(() => Object.fromEntries((assets ?? []).map((a) => [a.id, a])), [assets]);
  const fontAssets = (assets ?? []).filter((a) => a.kind === 'font' && a.status === 'active');
  const set = <K extends keyof BrandKit>(k: K, v: BrandKit[K]) => setKit((x) => ({ ...x, [k]: v }));

  const badColors = kit.colors.some((c) => !HEX_RE.test(c.hex));
  const submit = (e: React.FormEvent) => { e.preventDefault(); if (!badColors) onSave(kit, note); };

  return (
    <form className="stack" onSubmit={submit} aria-label="עריכת ערכת מותג">
      <div className="alert info small">שמירה יוצרת <strong>גרסה חדשה</strong> של המותג. קמפיינים קיימים נשארים על הגרסה שלהם עד לעדכון מפורש.</div>

      <fieldset className="box">
        <legend>לוגואים</legend>
        <div className="small muted" style={{ marginBottom: 6 }}>בחירה מתוך קובצי הלקוח בלבד. לוגו אינו נוצר מחדש באמצעות AI.</div>
        {kit.logos.length === 0 && <div className="small muted">לא נבחר לוגו.</div>}
        {kit.logos.map((l, i) => {
          const a = byId[l.assetId];
          return (
            <div className="row" key={i} style={{ padding: '4px 0' }}>
              {a ? <AssetThumb asset={a} size={36} /> : <span className="badge err">נכס חסר</span>}
              <span className="grow small">{a?.name ?? l.assetId}</span>
              <label className="sr-only" htmlFor={`lv-${i}`}>סוג לוגו</label>
              <select id={`lv-${i}`} value={l.variant} onChange={(e) => set('logos', kit.logos.map((x, j) => (j === i ? { ...x, variant: e.target.value as typeof x.variant } : x)))}>
                {Object.entries(LOGO_VARIANTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button type="button" className="btn small danger" onClick={() => set('logos', kit.logos.filter((_, j) => j !== i))}>הסרה</button>
            </div>
          );
        })}
        <button type="button" className="btn small" onClick={() => setPicking('logo')}>הוספת לוגו מהספרייה</button>
      </fieldset>

      <fieldset className="box">
        <legend>צבעים</legend>
        {kit.colors.map((c, i) => {
          const bad = !HEX_RE.test(c.hex);
          return (
            <div className="color-row" key={i}>
              <input type="color" aria-label={`בורר צבע ${i + 1}`} value={HEX_RE.test(c.hex) ? c.hex : '#000000'} onChange={(e) => set('colors', kit.colors.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))} />
              <input className="hex mono" aria-label={`קוד HEX ${i + 1}`} aria-invalid={bad} value={c.hex} onChange={(e) => set('colors', kit.colors.map((x, j) => (j === i ? { ...x, hex: e.target.value.trim() } : x)))} style={{ borderColor: bad ? 'var(--danger)' : undefined }} />
              <select aria-label={`תפקיד צבע ${i + 1}`} value={c.role} onChange={(e) => set('colors', kit.colors.map((x, j) => (j === i ? { ...x, role: e.target.value as typeof x.role } : x)))}>
                {Object.entries(COLOR_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input aria-label={`שם צבע ${i + 1}`} placeholder="שם (לא חובה)" value={c.name ?? ''} onChange={(e) => set('colors', kit.colors.map((x, j) => (j === i ? { ...x, name: e.target.value || undefined } : x)))} style={{ flex: '1 1 100px' }} />
              <button type="button" className="btn small danger" onClick={() => set('colors', kit.colors.filter((_, j) => j !== i))} aria-label={`הסרת צבע ${i + 1}`}>הסרה</button>
            </div>
          );
        })}
        {badColors && <div className="small" style={{ color: 'var(--danger)' }}>קוד HEX חייב להיות בפורמט #RRGGBB</div>}
        <button type="button" className="btn small" onClick={() => set('colors', [...kit.colors, { hex: '#000000', role: 'other' }])}>הוספת צבע</button>
      </fieldset>

      <fieldset className="box">
        <legend>פונטים</legend>
        {kit.fonts.map((f, i) => {
          const fa = f.assetId ? byId[f.assetId] : null;
          const licenseProblem = f.embedLicensed && (!fa || !fa.font_embed_allowed);
          const upd = (patch: Partial<typeof f>) => set('fonts', kit.fonts.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          return (
            <div key={i} className="stack" style={{ borderBottom: '1px solid var(--border)', padding: '8px 0' }}>
              <div className="grid cols-3">
                <Field label="משפחה"><input className="ltr" value={f.family} onChange={(e) => upd({ family: e.target.value })} /></Field>
                <Field label="משקלים" hint="לדוגמה: 400, 700"><input className="ltr" value={f.weights.join(', ')} onChange={(e) => upd({ weights: e.target.value.split(',').map((w) => parseInt(w.trim(), 10)).filter((n) => Number.isFinite(n)) })} /></Field>
                <Field label="תפקיד"><select value={f.role} onChange={(e) => upd({ role: e.target.value as typeof f.role })}>{Object.entries(FONT_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
              </div>
              <Field label="קובץ פונט">
                <select value={f.assetId ?? ''} onChange={(e) => upd({ assetId: e.target.value || null })}>
                  <option value="">ללא קובץ (שם בלבד — לא יוטמע)</option>
                  {fontAssets.map((a) => <option key={a.id} value={a.id}>{a.name}{a.font_embed_allowed ? ' · הטמעה מותרת' : ' · ללא רישיון הטמעה'}</option>)}
                </select>
              </Field>
              <Check checked={f.embedLicensed} onChange={(v) => upd({ embedLicensed: v })}>מותר להטמיע בבאנרים (רישיון הטמעה קיים)</Check>
              {licenseProblem && <div className="alert warn small">{!fa ? 'אין קובץ פונט — אי אפשר להטמיע פונט בלי קובץ מורשה. אין טעינת פונט מרחוק.' : 'בקובץ הפונט לא סומן רישיון הטמעה. ודאו רישיון לפני מסירה; אחרת הפונט לא ייכלל ב-ZIP.'}</div>}
              <div><button type="button" className="btn small danger" onClick={() => set('fonts', kit.fonts.filter((_, j) => j !== i))}>הסרת פונט</button></div>
            </div>
          );
        })}
        <button type="button" className="btn small" onClick={() => set('fonts', [...kit.fonts, { family: '', weights: [400], assetId: null, embedLicensed: false, role: 'other' }])}>הוספת פונט</button>
      </fieldset>

      <fieldset className="box">
        <legend>כללי לוגו וטיפוגרפיה</legend>
        <div className="grid cols-3">
          <Field label="מרווח ריק סביב הלוגו (px)"><NumInput value={kit.logoClearSpacePx} onChange={(v) => set('logoClearSpacePx', v)} nullable /></Field>
          <Field label="רוחב לוגו מינימלי (px)"><NumInput value={kit.logoMinWidthPx} onChange={(v) => set('logoMinWidthPx', v)} nullable /></Field>
          <Field label="גודל פונט מינימלי (px)"><NumInput value={kit.minFontSizePx} onChange={(v) => set('minFontSizePx', v ?? 10)} min={4} /></Field>
        </div>
      </fieldset>

      <div className="grid cols-2">
        <Field label="סגנון תמונות"><textarea value={kit.imageStyle} onChange={(e) => set('imageStyle', e.target.value)} /></Field>
        <Field label="סגנון אנימציה"><textarea value={kit.animationStyle} onChange={(e) => set('animationStyle', e.target.value)} /></Field>
      </div>
      <Field label="הנחיות תוכן"><textarea value={kit.contentGuidelines} onChange={(e) => set('contentGuidelines', e.target.value)} /></Field>
      <Field label="טקסט מדריך המותג" hint="נשמר לצד השדות המובנים"><textarea value={kit.guideText} onChange={(e) => set('guideText', e.target.value)} style={{ minHeight: 140 }} /></Field>

      <fieldset className="box">
        <legend>דוגמאות מאושרות</legend>
        <div className="row">
          {kit.approvedExampleAssetIds.map((id) => (
            <span key={id} className="row" style={{ gap: 4 }}>
              {byId[id] ? <AssetThumb asset={byId[id]} size={36} /> : <span className="mono small">{id}</span>}
              <button type="button" className="btn small danger" aria-label="הסרת דוגמה" onClick={() => set('approvedExampleAssetIds', kit.approvedExampleAssetIds.filter((x) => x !== id))}>✕</button>
            </span>
          ))}
          <button type="button" className="btn small" onClick={() => setPicking('example')}>הוספת דוגמה</button>
        </div>
      </fieldset>

      <Field label="הערת גרסה"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="מה השתנה?" maxLength={500} /></Field>
      <ErrorBox error={error} />
      <div className="row">
        <button className="btn primary" disabled={busy || badColors}>{busy ? 'שומר…' : 'שמירה כגרסה חדשה'}</button>
        <button type="button" className="btn" onClick={onCancel}>ביטול</button>
      </div>

      {picking && (
        <AssetPicker clientId={clientId} title={picking === 'logo' ? 'בחירת לוגו' : 'בחירת דוגמאות מאושרות'}
          exclude={picking === 'logo' ? kit.logos.map((l) => l.assetId) : kit.approvedExampleAssetIds}
          onClose={() => setPicking(null)}
          onPick={(ids) => {
            if (picking === 'logo') set('logos', [...kit.logos, ...ids.map((id) => ({ assetId: id, variant: (kit.logos.length ? 'other' : 'primary') as 'other' | 'primary' }))]);
            else set('approvedExampleAssetIds', [...kit.approvedExampleAssetIds, ...ids]);
            setPicking(null);
          }} />
      )}
    </form>
  );
}

function NumInput({ value, onChange, nullable, min = 0 }: { value: number | null; onChange: (v: number | null) => void; nullable?: boolean; min?: number }) {
  return <input type="number" className="ltr" min={min} value={value ?? ''} placeholder={nullable ? 'לא מוגדר' : undefined} onChange={(e) => onChange(e.target.value === '' ? (nullable ? null : min) : Number(e.target.value))} />;
}

/** תצוגה לקריאה בלבד של ערכה */
export function BrandKitView({ kit, assets }: { kit: BrandKit; assets: Record<string, Asset> }) {
  const k = normalizeKit(kit);
  return (
    <div className="stack">
      <div>
        <h3>לוגואים</h3>
        {k.logos.length === 0 ? <div className="small muted">אין לוגו בערכה.</div> : (
          <div className="row">{k.logos.map((l, i) => <div key={i} className="stack" style={{ gap: 2, alignItems: 'center' }}>{assets[l.assetId] ? <AssetThumb asset={assets[l.assetId]} /> : <span className="badge err">חסר</span>}<span className="small">{LOGO_VARIANTS[l.variant]}</span></div>)}</div>
        )}
      </div>
      <div>
        <h3>צבעים</h3>
        {k.colors.length === 0 ? <div className="small muted">אין צבעים.</div> : (
          <div className="row">{k.colors.map((c, i) => <span key={i} className="row" style={{ gap: 4 }}><span className="swatch" style={{ background: c.hex }} aria-hidden="true" /><span className="mono">{c.hex}</span><span className="small muted">{COLOR_ROLES[c.role]}{c.name ? ` · ${c.name}` : ''}</span></span>)}</div>
        )}
      </div>
      <div>
        <h3>פונטים</h3>
        {k.fonts.length === 0 ? <div className="small muted">אין פונטים.</div> : (
          <ul className="list-plain">{k.fonts.map((f, i) => (
            <li key={i} className="row"><strong className="ltr">{f.family || '—'}</strong><span className="small mono">{f.weights.join(',')}</span><span className="small muted">{FONT_ROLES[f.role]}</span>
              {f.assetId ? <span className="badge info">קובץ</span> : <span className="badge">שם בלבד</span>}
              {f.embedLicensed ? <span className="badge ok">הטמעה מורשית</span> : <span className="badge warn">לא להטמעה</span>}</li>
          ))}</ul>
        )}
      </div>
      <details>
        <summary className="small muted" style={{ cursor: 'pointer' }}>כללי מותג מתקדמים (מרווחים, סגנון, הנחיות)</summary>
      <dl className="kv" style={{ marginTop: 8 }}>
        <dt>מרווח לוגו</dt><dd>{k.logoClearSpacePx ?? 'לא מוגדר'}{k.logoClearSpacePx != null && ' px'}</dd>
        <dt>רוחב לוגו מינימלי</dt><dd>{k.logoMinWidthPx ?? 'לא מוגדר'}{k.logoMinWidthPx != null && ' px'}</dd>
        <dt>פונט מינימלי</dt><dd>{k.minFontSizePx} px</dd>
        <dt>סגנון תמונות</dt><dd>{k.imageStyle || '—'}</dd>
        <dt>סגנון אנימציה</dt><dd>{k.animationStyle || '—'}</dd>
        <dt>הנחיות תוכן</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{k.contentGuidelines || '—'}</dd>
        <dt>מדריך מותג</dt><dd style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{k.guideText || '—'}</dd>
        <dt>דוגמאות מאושרות</dt><dd>{k.approvedExampleAssetIds.length}</dd>
      </dl>
      </details>
    </div>
  );
}
